// Touch-only "swipe down to minimize" gesture for full-screen video / movie /
// audio players. Returns a `{ onTouchStart, onTouchMove, onTouchEnd, style }`
// bundle to spread onto the element you want the gesture to live on (usually
// the stage / video container — NOT the whole modal, so scrolling the
// comments / details panel below stays untouched).
//
// Heuristic — mimics YouTube / Instagram:
//   • Pure vertical-down drag (|dx| < |dy| * 0.6 AND dy > 0)
//   • Move ≥ MIN_DRAG px before we consider it a "swipe"
//   • Either total drag ≥ DISMISS_PX  OR  flick velocity ≥ DISMISS_V
//     → fire onMinimize() exactly once
//   • While dragging we translateY the element so the user gets visual
//     feedback (it "follows" their thumb). On release we either commit
//     (let onMinimize swap out the modal) or snap back to translateY(0).
//
// Pointer events are intentionally NOT bound — on desktop a swipe gesture
// is way less natural and we don't want to interfere with mouse drag-to-
// select or click-and-drag scrubbing. The dedicated "Mini Player" pill
// button still works for everyone.
import { useCallback, useRef, useState } from "react";

const MIN_DRAG = 8;       // ignore micro jitter
const DISMISS_PX = 110;   // total down-drag to commit
const DISMISS_V = 0.55;   // px-per-ms flick velocity to commit
const RUBBER_BAND_RATIO = 0.92; // 0.92 = "slightly slower than your thumb"

export default function useSwipeDownToMinimize(onMinimize, { enabled = true } = {}) {
  const start = useRef(null);          // { y, x, t }
  const lastMove = useRef(null);       // { y, t } for velocity
  const firedRef = useRef(false);
  const [dragY, setDragY] = useState(0);

  const reset = useCallback(() => {
    start.current = null;
    lastMove.current = null;
    firedRef.current = false;
    setDragY(0);
  }, []);

  const onTouchStart = useCallback((e) => {
    if (!enabled) return;
    const t = e.touches && e.touches[0];
    if (!t) return;
    start.current = { x: t.clientX, y: t.clientY, t: Date.now() };
    lastMove.current = { y: t.clientY, t: Date.now() };
    firedRef.current = false;
  }, [enabled]);

  const onTouchMove = useCallback((e) => {
    if (!enabled || !start.current || firedRef.current) return;
    const t = e.touches && e.touches[0];
    if (!t) return;
    const dx = t.clientX - start.current.x;
    const dy = t.clientY - start.current.y;
    // Only react to vertical-DOWN drags. Vertical-UP or horizontal swipes
    // pass through to whatever else the user is doing (scrubbing, panning).
    if (dy < MIN_DRAG) return;
    if (Math.abs(dx) > Math.abs(dy) * 0.6) return;
    // We're committing to the gesture — set the translate AND stop the
    // browser from interpreting the same drag as a page scroll.
    setDragY(dy * RUBBER_BAND_RATIO);
    lastMove.current = { y: t.clientY, t: Date.now() };
  }, [enabled]);

  const onTouchEnd = useCallback(() => {
    if (!enabled || !start.current || firedRef.current) {
      reset();
      return;
    }
    const now = Date.now();
    const totalDy = (lastMove.current?.y ?? start.current.y) - start.current.y;
    const dt = Math.max(1, (lastMove.current?.t ?? now) - start.current.t);
    const v = totalDy / dt; // px/ms
    if (totalDy >= DISMISS_PX || (totalDy >= 40 && v >= DISMISS_V)) {
      firedRef.current = true;
      try { onMinimize && onMinimize(); } catch (_) {}
      // Let parent unmount the modal; reset internal state.
      setTimeout(reset, 0);
      return;
    }
    // Snap back.
    setDragY(0);
    reset();
  }, [enabled, onMinimize, reset]);

  // The element should apply this transform so the user sees the modal
  // follow their thumb. `transition` is none during the drag and a quick
  // ease on release so the snap-back feels crisp.
  const style = dragY > 0
    ? { transform: `translate3d(0, ${dragY}px, 0)`, transition: "none", touchAction: "none" }
    : { transform: "translate3d(0, 0, 0)", transition: "transform 180ms cubic-bezier(.2,.8,.2,1)" };

  return { onTouchStart, onTouchMove, onTouchEnd, style, dragY };
}
